import { Link } from "react-router-dom";
import {
  ArrowRight,
  BarChart3,
  BellRing,
  BookOpen,
  CheckCircle2,
  ChevronDown,
  Cpu,
  GitPullRequest,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";
import { cn } from "@/lib/cn";

const ctaPrimary = cn(
  "inline-flex h-11 items-center justify-center gap-2 rounded-md bg-primary px-8 text-base font-medium text-primary-foreground transition-colors hover:bg-primary/90",
);

const ctaOutline = cn(
  "inline-flex h-11 items-center justify-center gap-2 rounded-md border border-input bg-background px-8 text-base font-medium transition-colors hover:bg-accent hover:text-accent-foreground",
);

const FEATURES = [
  {
    icon: Users,
    title: "한 작업, 여러 사람, 하나의 PR.",
    body: "팀원이 동시에 같은 기능을 나눠 맡아도 결과는 깔끔한 PR 한 건으로 모입니다. 충돌이 생기면 별도 태스크로 분리해 AI가 정리를 이어갑니다.",
  },
  {
    icon: Cpu,
    title: "AI 는 당신의 자리에서 동작합니다.",
    body: "맡은 태스크는 본인 디바이스의 AI 가 실행합니다. 격리된 작업 폴더에서 시작·종료되어 다른 멤버의 작업과 섞이지 않고, 코드와 API 키는 클라우드로 나가지 않습니다.",
  },
  {
    icon: GitPullRequest,
    title: "코드 리뷰는 AI 가 먼저.",
    body: "PR 모니터의 버튼 한 번이면 변경된 줄마다 한국어 리뷰가 달립니다. 사람은 한 번 걸러진 PR 만 검토하면 됩니다.",
  },
  {
    icon: BarChart3,
    title: "이번 주 성과가 한 장으로.",
    body: "머지된 PR, 완료한 작업, AI 비용을 멤버별로 지난주와 비교해 보여 줍니다. 버튼 한 번으로 복사해 팀 메신저에 바로 공유하세요.",
  },
  {
    icon: ShieldCheck,
    title: "깨진 코드는 main 까지 가지 못합니다.",
    body: "프로젝트에 타입체크·빌드 같은 검증 명령을 등록해 두면, 통과하지 못한 변경으로는 PR을 만들지 않습니다. 실패하면 AI가 먼저 원인을 고쳐 보고 다시 검증합니다.",
  },
  {
    icon: BookOpen,
    title: "팀의 결정이 쌓이고, AI가 기억합니다.",
    body: "PR이 머지될 때마다 무엇을 왜 만들었는지와 리뷰 의견이 작업 기록으로 남습니다. 팀은 검색해서 찾아보고, AI는 다음 작업에서 같은 실수를 반복하지 않습니다.",
  },
];

const ERROR_WATCH_POINTS = [
  {
    title: "같은 에러는 한 번만",
    body: "수십 번 울려도 작업은 하나입니다. 이미 고치는 중이거나 PR이 올라간 에러는 횟수만 셉니다.",
  },
  {
    title: "고친 뒤에 또 나면 재발로 표시",
    body: "수정이 머지된 뒤 다시 발생하면 이전 수정과 연결된 재발 작업을 따로 만듭니다.",
  },
  {
    title: "토큰과 로그는 내 컴퓨터에서",
    body: "슬랙 토큰은 내 컴퓨터 키체인에만 저장되고, 로그에 섞인 토큰과 키는 가린 뒤 작업으로 옮깁니다.",
  },
];

/**
 * 실제 데스크톱 앱 캡처 (public/screenshots). 로컬 데모 데이터로 찍은 화면이며,
 * 앱 UI 가 바뀌면 다시 캡처해 교체한다.
 */
const FLOW_STEPS = [
  {
    step: "01",
    title: "만들 것을 한 줄로 적습니다",
    body: "요구사항을 적으면 AI가 기존 코드를 직접 읽고 기획서를 만든 뒤, 실행할 수 있는 하위 작업으로 나눕니다. 코드에 없는 파일을 가리키는 항목은 자동으로 걸러집니다.",
    image: { src: "/screenshots/new-task.webp", width: 1120, height: 922, alt: "새 태스크 화면에 요구사항을 한 줄 적은 모습" },
  },
  {
    step: "02",
    title: "팀이 나눠 맡고, 각자의 AI가 만듭니다",
    body: "하위 작업마다 담당자를 정하면 그 사람의 컴퓨터에서 AI가 코드를 작성합니다. 어떤 작업이 끝났고 어디서 멈췄는지 한 화면에서 보입니다.",
    image: { src: "/screenshots/plan.webp", width: 1440, height: 866, alt: "플랜 상세에서 하위 작업 네 개의 진행 상태를 보여 주는 화면" },
  },
  {
    step: "03",
    title: "확인하고, PR 하나로 받습니다",
    body: "AI가 만든 결과는 '오늘' 화면에 모입니다. 확인하고 승인하면 팀원들의 결과가 하나의 브랜치로 합쳐져 PR 한 건으로 올라갑니다.",
    image: { src: "/screenshots/today.webp", width: 1600, height: 438, alt: "오늘 화면에 검토가 필요한 작업이 모여 있는 모습" },
  },
];

const TEAM_SHOTS = [
  {
    title: "월요일 아침, 지난주가 한 화면에",
    body: "머지된 PR, 완료한 작업, AI 비용을 멤버별·프로젝트별로 지난주와 비교합니다. 공유용 복사 한 번이면 팀 채널에 그대로 붙여 넣을 수 있습니다.",
    image: { src: "/screenshots/weekly-report.webp", width: 1600, height: 926, alt: "주간 리포트 화면에 머지된 PR 7건과 멤버별 AI 비용이 보이는 모습" },
  },
  {
    title: "왜 그렇게 만들었는지 남습니다",
    body: "PR이 머지될 때마다 무엇을, 왜 만들었는지와 리뷰 의견이 팀 저장소에 기록됩니다. AI는 다음 작업에서 관련 기록을 찾아 같은 지적을 반복하지 않습니다.",
    image: { src: "/screenshots/notes.webp", width: 1600, height: 741, alt: "작업 기록 화면에 결제 기능의 결정 이유와 리뷰 의견이 정리된 모습" },
  },
];

export function LandingPage(): JSX.Element {
  return (
    <main>
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-b from-accent/40 via-background to-background"
          aria-hidden
        />
        <div className="container flex flex-col items-center py-24 text-center sm:py-32">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-border bg-background/60 px-3 py-1 text-xs text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5" aria-hidden />
            AI로 개발하는 팀을 위한 데스크톱 앱
          </div>
          <h1 className="max-w-3xl text-4xl font-bold tracking-tight sm:text-5xl md:text-6xl">
            커피 한 잔 하고 오면,<br />
            PR이 와 있습니다.
          </h1>
          <p className="mt-6 max-w-2xl text-base text-muted-foreground sm:text-lg">
            Devit은 개발팀을 위한 AI 작업 보드입니다. 만들 것을 적어 두거나 에러 알림이 오면, 팀원
            각자의 컴퓨터에서 AI가 코드를 작성하고 검증까지 마친 PR로 올려 둡니다.
          </p>
          <div className="mt-10 flex flex-col items-center gap-3 sm:flex-row">
            <Link to="/signup" className={ctaPrimary}>
              무료로 시작하기 <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/download" className={ctaOutline}>
              데스크톱 앱 다운로드
            </Link>
          </div>
          <ul className="mt-8 flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs text-muted-foreground">
            <li className="flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-primary" aria-hidden />
              혼자 시작해서 팀으로 자라기
            </li>
            <li className="flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-primary" aria-hidden />
              코드와 API 키는 본인 디바이스에만
            </li>
            <li className="flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5 text-primary" aria-hidden />
              macOS · Windows
            </li>
          </ul>
          <ProductShot
            src="/screenshots/board.webp"
            width={2400}
            height={1290}
            alt="Devit 데스크톱 앱의 태스크 보드. 팀원별로 맡은 작업이 할 일, 진행 중, 리뷰, 완료 열에 놓여 있다"
            className="mt-14 w-full max-w-6xl"
            eager
          />
        </div>
      </section>

      {/* Flow — 실제 화면 */}
      <section className="border-t border-border bg-muted/30 py-20">
        <div className="container">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              한 줄에서 PR 까지, 세 단계.
            </h2>
            <p className="mt-4 text-muted-foreground">사람은 의도를, 도구는 나머지를.</p>
          </div>
          <ol className="mx-auto mt-16 max-w-6xl space-y-20">
            {FLOW_STEPS.map((s, i) => (
              <li
                key={s.step}
                className="grid items-center gap-8 lg:grid-cols-5 lg:gap-12"
              >
                <div className={cn("lg:col-span-2", i % 2 === 1 && "lg:order-2")}>
                  <div className="font-mono text-sm text-muted-foreground">{s.step}</div>
                  <h3 className="mt-2 text-2xl font-semibold tracking-tight">{s.title}</h3>
                  <p className="mt-3 leading-relaxed text-muted-foreground">{s.body}</p>
                </div>
                <ProductShot
                  {...s.image}
                  className={cn("lg:col-span-3", i % 2 === 1 && "lg:order-1")}
                />
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* 에러 알림 연동 */}
      <section className="py-20" id="error-watch">
        <div className="container">
          <div className="mx-auto grid max-w-5xl items-center gap-10 lg:grid-cols-2">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-border px-3 py-1 text-xs text-muted-foreground">
                <BellRing className="h-3.5 w-3.5" aria-hidden />
                슬랙 에러 알림 연동
              </div>
              <h2 className="mt-4 text-3xl font-bold tracking-tight sm:text-4xl">
                에러가 나면, AI가 먼저 고쳐 둡니다.
              </h2>
              <p className="mt-4 leading-relaxed text-muted-foreground">
                서비스가 슬랙으로 보내는 에러 알림을 Devit이 읽고, 새 에러마다 수정 작업을 만듭니다.
                AI가 원인을 찾아 고치고, 검증을 통과한 코드만 PR로 올라옵니다. 에러 로그를 뒤지는 대신
                고쳐진 코드를 리뷰하면 됩니다.
              </p>
            </div>
            <ul className="space-y-4">
              {ERROR_WATCH_POINTS.map((point) => (
                <li key={point.title} className="rounded-lg border border-border bg-card p-5">
                  <p className="font-medium">{point.title}</p>
                  <p className="mt-1 text-sm text-muted-foreground">{point.body}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* 팀 리포트·작업 기록 */}
      <section className="border-t border-border py-20" id="team">
        <div className="container">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              팀이 한 일이 쌓이고, 보입니다.
            </h2>
          </div>
          <ol className="mx-auto mt-16 max-w-6xl space-y-20">
            {TEAM_SHOTS.map((s, i) => (
              <li key={s.title} className="grid items-center gap-8 lg:grid-cols-5 lg:gap-12">
                <div className={cn("lg:col-span-2", i % 2 === 1 && "lg:order-2")}>
                  <h3 className="text-2xl font-semibold tracking-tight">{s.title}</h3>
                  <p className="mt-3 leading-relaxed text-muted-foreground">{s.body}</p>
                </div>
                <ProductShot
                  {...s.image}
                  className={cn("lg:col-span-3", i % 2 === 1 && "lg:order-1")}
                />
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Features */}
      <section className="py-20">
        <div className="container">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              더 빠르게, 그러나 더 안전하게.
            </h2>
            <p className="mt-4 text-muted-foreground">
              속도가 올라가도 깨진 코드는 흘러가지 않습니다.
            </p>
          </div>
          <div className="mx-auto mt-12 grid max-w-5xl grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((f) => (
              <div
                key={f.title}
                className="rounded-lg border border-border bg-card p-6 shadow-sm"
              >
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <f.icon className="h-5 w-5" aria-hidden />
                </div>
                <h3 className="mt-4 text-lg font-semibold">{f.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Faq />

      {/* Final CTA */}
      <section className="border-t border-border py-20" id="final-cta">
        <div className="container text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            오늘, 첫 PR 을 받아 보세요.
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            혼자 시작했다가, 팀이 늘어도 그대로 이어집니다.
          </p>
          <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <Link to="/signup" className={ctaPrimary}>
              무료 가입 <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/login" className={ctaOutline}>
              이미 계정이 있어요
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}

/** 결제·도입 전에 반복해서 나오는 질문. 답변은 실제 동작과 맞춘다 (저장 항목은 개인정보 처리방침과 동일). */
const FAQ_ITEMS: { q: string; a: React.ReactNode }[] = [
  {
    q: "코드가 Devit 서버로 올라가나요?",
    a: "아니요. 서버에는 태스크 제목과 설명, 담당자, 진행 상태, PR 주소 같은 작업 정보만 저장됩니다. 코드와 API 키, GitHub 토큰은 내 컴퓨터에만 있고, AI를 실행할 때 코드는 내 컴퓨터에서 내가 고른 AI 서비스로 바로 전달됩니다.",
  },
  {
    q: "무엇이 있어야 쓸 수 있나요?",
    a: (
      <>
        macOS 또는 Windows 컴퓨터, Git과 GitHub 저장소, 그리고 AI 하나(Claude Code, OpenAI 키,
        Gemini 키 중 선택)가 필요합니다. 자세한 내용은{" "}
        <Link to="/download" className="underline underline-offset-4 hover:text-foreground">
          다운로드 페이지
        </Link>
        에 정리해 두었습니다.
      </>
    ),
  },
  {
    q: "AI 요금은 누가 내나요?",
    a: "연결한 AI 서비스에 직접 냅니다. Claude Code를 이미 구독 중이라면 그대로 쓰면 되고, Devit이 AI 요금을 따로 받지는 않습니다.",
  },
  {
    q: "AI가 만든 코드가 바로 머지되나요?",
    a: "아니요. 결과는 PR로 올라오고, 머지는 사람이 결정합니다. 기본 설정인 승인 모드에서는 AI가 만든 변경을 내가 확인하고 승인해야 커밋과 PR로 이어집니다.",
  },
  {
    q: "슬랙 에러 알림은 어떻게 연결하나요?",
    a: "슬랙에 읽기 권한만 있는 봇을 하나 만들어 에러 채널에 초대하고, 봇 토큰을 Devit 설정에 붙여 넣으면 됩니다. 이후 프로젝트 설정에서 채널을 고르면 1분마다 새 에러를 확인합니다. 방법은 앱 설정 화면에 단계별로 안내되어 있습니다.",
  },
  {
    q: "작업 기록은 어디에 저장되나요?",
    a: "팀이 정한 별도의 GitHub 저장소에 커밋됩니다. 코드 저장소와 섞이지 않고, Devit 서버에는 저장하지 않습니다. 저장소를 정하지 않으면 내 컴퓨터에만 남습니다.",
  },
  {
    q: "혼자서도 쓸 수 있나요?",
    a: "네. 가입하면 개인 워크스페이스가 바로 만들어지고, 팀원은 필요할 때 초대하면 됩니다.",
  },
  {
    q: "지금 비용이 드나요?",
    a: "지금은 모든 기능이 무료입니다. 유료 플랜을 도입하면 가격 페이지에 미리 알리고, 직접 결제하기 전에는 요금이 청구되지 않습니다.",
  },
];

function Faq(): JSX.Element {
  return (
    <section className="border-t border-border bg-muted/30 py-20" id="faq">
      <div className="container">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">자주 묻는 질문</h2>
        </div>
        <div className="mx-auto mt-10 max-w-2xl divide-y divide-border rounded-lg border border-border bg-card">
          {FAQ_ITEMS.map((item) => (
            <details key={item.q} className="group px-5 py-4">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-medium [&::-webkit-details-marker]:hidden">
                {item.q}
                <ChevronDown
                  className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180"
                  aria-hidden
                />
              </summary>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

/** 실제 앱 캡처를 창 모양 테두리에 담아 보여 준다. width/height 는 파일의 실제 픽셀 크기 (로딩 중 자리 확보용). */
function ProductShot({
  src,
  alt,
  width,
  height,
  className,
  eager = false,
}: {
  src: string;
  alt: string;
  width: number;
  height: number;
  className?: string;
  eager?: boolean;
}): JSX.Element {
  return (
    <figure
      className={cn(
        "overflow-hidden rounded-xl border border-border bg-card shadow-xl shadow-foreground/5",
        className,
      )}
    >
      <div className="flex items-center gap-1.5 border-b border-border bg-muted/50 px-4 py-2.5" aria-hidden>
        <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
        <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
        <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
      </div>
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        className="block h-auto w-full"
      />
    </figure>
  );
}
